import { useEffect, useState } from "react";
import { ArrowRight, Check, Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { getFooter } from "../services/api";

interface Contact {
  email?: string;
  phone_number?: string;
}

const FALLBACK: Contact = {
  email: "bookings@beautybycharme.com",
  phone_number: "+1 (902) 989-5769",
};

const STEPS = [
  {
    title: "We'll review your request",
    body: "Our team carefully goes through your requirements to prepare a personalised quote.",
  },
  {
    title: "You'll receive your quote",
    body: "We'll email you detailed pricing and availability, usually within 24 hours.",
  },
  {
    title: "Confirm your booking",
    body: "Once you're happy with the quote, we'll finalise your session and send a confirmation.",
  },
];

export default function ThankYouPage() {
  const [contact, setContact] = useState<Contact>(FALLBACK);

  useEffect(() => {
    window.scrollTo(0, 0);
    let active = true;
    getFooter()
      .then((res: any) => {
        const d = res?.data?.data?.[0];
        if (active && d && (d.email || d.phone_number)) {
          setContact({ email: d.email, phone_number: d.phone_number });
        }
      })
      .catch(() => {
        /* keep the sensible fallback */
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FBEFEF] px-4 pt-20">
      <div className="animate-charme-rise mx-auto max-w-xl py-12 md:py-20">
        {/* The resolving moment */}
        <div className="flex flex-col items-center text-center">
          <div
            className="animate-charme-pop flex h-20 w-20 items-center justify-center rounded-full bg-[#B87775]/15"
            style={{ animationDelay: "120ms" }}
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#B87775]">
              <Check className="h-7 w-7 text-white" aria-hidden="true" />
            </span>
          </div>
          <p className="mt-6 text-xs uppercase tracking-[0.3em] text-[#B87775]">
            Quote requested
          </p>
          <h1 className="mt-3 font-serif text-4xl leading-tight text-[#3a2e2c]">
            Thank you
          </h1>
          <p className="mt-3 max-w-md text-[#7c6c6a]">
            Your request is in. We'll be in touch within 24 hours with pricing
            and availability.
          </p>
        </div>

        {/* What happens next */}
        <div className="mt-10 rounded-2xl border border-[#EAD7D6] bg-white p-6 md:p-8">
          <h2 className="font-serif text-xl text-[#3a2e2c]">What happens next</h2>
          <ol className="mt-5 space-y-5">
            {STEPS.map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#FBEFEF] text-sm tabular-nums text-[#B87775]">
                  {i + 1}
                </span>
                <div>
                  <p className="font-medium text-[#3a2e2c]">{step.title}</p>
                  <p className="mt-0.5 text-[15px] leading-relaxed text-[#7c6c6a]">
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Contact */}
        <div className="mt-8 text-center">
          <p className="text-sm text-[#9A6B69]">
            Any questions in the meantime? Reach us directly.
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            {contact.email && (
              <a
                href={`mailto:${contact.email}`}
                className="inline-flex items-center gap-2 text-[#B87775] hover:text-[#a96764]"
              >
                <Mail className="h-4 w-4" aria-hidden="true" />
                {contact.email}
              </a>
            )}
            {contact.phone_number && (
              <a
                href={`tel:${contact.phone_number}`}
                className="inline-flex items-center gap-2 text-[#B87775] hover:text-[#a96764]"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                {contact.phone_number}
              </a>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#B87775] px-6 py-3 text-sm font-medium text-white transition hover:bg-[#a96764] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
          >
            Back to homepage
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <Link
            to="/gallery"
            className="inline-flex items-center justify-center rounded-full border border-[#E3C9C8] px-6 py-3 text-sm font-medium text-[#B87775] transition hover:border-[#B87775] hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B87775] focus-visible:ring-offset-2"
          >
            View our gallery
          </Link>
        </div>
      </div>
    </div>
  );
}
